import { get } from 'lodash'

/**
 * Управление выпадающим списком с клавиатуры: свободный ввод по
 * клавишам-разделителям, перемещение курсора по списку и выбор
 * подсвеченного пункта.
 */
export const keyboard = {
  /**
   * Локальное состояние управления с клавиатуры.
   * Поле _cursorPosition хранит позицию курсора в отфильтрованном
   * списке; значение -1 означает, что ни один пункт не подсвечен.
   *
   * @returns {{ _cursorPosition: number }} Локальное состояние.
   */
  data() {
    return {
      _cursorPosition: -1
    }
  },

  computed: {
    /**
     * Собирает обработчики событий корневого элемента.
     * Клавиатура слушается на корне только когда своего поля ввода нет.
     * Если поле ввода есть, события приходят от него, а обработчик на корне
     * поймал бы то же самое событие вторым шагом всплытия.
     *
     * @returns {object} Набор обработчиков для секции on.
     */
    rootListeners() {
      const result = { click: this._clickHandler }

      if (!this.hasInput) {
        result.keydown = this._onKeydown
        result.keyup = this._onKeyup
      }

      return result
    }
  },

  methods: {
    /**
     * Обрабатывает отпускание клавиши.
     *
     * Если isFreeInput и отпущенная клавиша входит в
     * freeInputTriggers и есть цель события и есть строка поиска, из
     * строки поиска берется часть до символа этой клавиши, а при
     * отсутствии символа - вся строка; непустая часть добавляется к
     * выбранным значениям, после чего строка поиска очищается.
     *
     * Если отпущена клавиша Tab, поле помечается как получившее
     * фокус и список раскрывается.
     *
     * @param {KeyboardEvent} event Событие клавиатуры.
     */
    _onKeyup(event) {
      if (this.isFreeInput) {
        const triggers = this.freeInputTriggers

        const checkKey = (keySample, event) => {
          return (
            typeof keySample === 'number' && event.keyCode === keySample ||
            typeof keySample === 'string' && event.key === keySample ||
            false
          )
        }

        if (
          (Array.isArray(triggers) && triggers.some(key => {
            return checkKey(key, event)
          }) ||
          (typeof triggers === 'string' || typeof triggers === 'number') && checkKey(triggers, event)) &&
          event.target &&
          this.search
        ) {
          let index = this.search.indexOf(event.key)

          if (!~index) {
            index = this.search.length
          }

          const valueToAdd = this.search.substring(0, index).trim()

          if (valueToAdd) {
            this._setValue(valueToAdd)
          }

          this._clearSearchValue()
        }
      }

      if (event.keyCode === 9) {
        this._data._focused = true
        this._data._showDropdown = true
      }
    },
    /**
     * Обрабатывает нажатие клавиши.
     *
     * Если нажата клавиша Tab, фокус и раскрытие списка снимаются.
     * Если при этом есть строка поиска и isFreeInput, при combobox
     * добавляется строка поиска, иначе - значение первого
     * незаблокированного пункта; если такого пункта нет, значение не
     * добавляется. Строка поиска очищается, после чего проверяется
     * обязательный выбор.
     *
     * Если ссылки на меню нет, обработка на этом заканчивается.
     *
     * Если нажата стрелка вниз или стрелка вверх, список
     * раскрывается и курсор сдвигается на один пункт вперед или
     * назад, а список прокручивается так, чтобы подсвеченный пункт
     * оставался видимым.
     *
     * Если нажата клавиша Enter, подсвеченный пункт добавляется к
     * выбранным значениям, если он не заблокирован. Если
     * подсвеченного пункта нет и isFreeInput, добавляется строка
     * поиска. Если нет multiple, список закрывается. Курсор и строка
     * поиска сбрасываются.
     *
     * @param {KeyboardEvent} event Событие клавиатуры.
     */
    _onKeydown(event) {
      if (event.keyCode === 9) {
        this._data._focused = false
        this._data._showDropdown = false

        if (this.search && this.isFreeInput) {
          let valueToAdd

          if (this.combobox) {
            valueToAdd = this.search
          } else {
            const item = this.filteredItems.find(({ _disabled }) => {
              return !_disabled
            })
            if (item) {
              valueToAdd = get(item, this.$_itemValue, item)
            }
          }

          if (valueToAdd) {
            this._setValue(valueToAdd)
          }
        }

        this._clearSearchValue()
        this._setErrorOnRequired()
      }

      if (this.menu) {
        if (event.keyCode === 40) {
          event.preventDefault()
          this._clickOnMain()
          this._moveCursor(1)
        }

        if (event.keyCode === 38) {
          event.preventDefault()
          this._clickOnMain()
          this._moveCursor(-1)
        }

        if (this.menu.$refs && this.menu.$refs[`menu-${this.menu._uid}`]) {
          const height = this.menu.$refs[`menu-${this.menu._uid}`].offsetHeight
          const scrollTop = this.menu.$refs[`menu-${this.menu._uid}`].scrollTop
          const currentPosition = this._data._cursorPosition * 48
          const bottomEdge = scrollTop + height
          const currentBottomEdge = currentPosition + 48 + 12
          const bottomEdgeDiff = currentBottomEdge - bottomEdge
          const topEdgeDiff = scrollTop - currentPosition + 12

          if (bottomEdgeDiff > 0) {
            this.menu.$refs[`menu-${this.menu._uid}`].scrollTop += bottomEdgeDiff
          } else if (topEdgeDiff > 0) {
            this.menu.$refs[`menu-${this.menu._uid}`].scrollTop -= topEdgeDiff
          }
        }

        if (event.keyCode === 13) {
          const item = this.filteredItems.find(({ _hovered }) => {
            return _hovered
          })

          if (item && !item._disabled) {
            this.addByValue(get(item, this.$_itemValue, item))
          } else if (!item && this.isFreeInput) {
            this.addByValue(this.search)
          }

          if (!this.multiple) {
            this._clickOutside()
          }

          this._data._cursorPosition = -1

          this._clearSearchValue()
        }
      }
    },

    /**
     * Сдвигает курсор клавиатуры по отфильтрованному списку по кругу.
     * Заблокированные пункты пропускаются. Если доступных пунктов нет,
     * курсор остается на месте.
     *
     * @param {number} step Шаг сдвига: 1 вниз, -1 вверх.
     * @returns {void} Ничего не возвращает, меняет позицию курсора.
     */
    _moveCursor(step) {
      const itemsLength = this.filteredItems.length
      if (!itemsLength) {
        return
      }

      let position = this._data._cursorPosition
      for (let attempt = 0; attempt < itemsLength; attempt++) {
        position += step
        if (position > itemsLength - 1) {
          position = 0
        }
        if (position < 0) {
          position = itemsLength - 1
        }

        if (!this.filteredItems[position]._disabled) {
          this._data._cursorPosition = position

          return
        }
      }
    }
  }
}
